import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Tab,
  Tabs,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  TextField,
  FormControl,
  Select,
  InputLabel,
  MenuItem,
  SelectChangeEvent,
  Tooltip,
  Collapse
} from '@mui/material';
import { 
  Timeline, 
  TimelineItem, 
  TimelineSeparator, 
  TimelineConnector, 
  TimelineContent, 
  TimelineDot, 
  TimelineOppositeContent 
} from '@mui/lab';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import MedicationIcon from '@mui/icons-material/Medication';
import ReceiptIcon from '@mui/icons-material/Receipt';
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useAuth } from '../context/AuthContext';
import { Job, getJobs, formatFormType, formatDate, getStatusColor, getTimelineDotColor } from '../api/jobsService';
import JobFormDataDialog from './JobFormDataDialog';

// Form types and their URLs
const FORMS = [
  {
    id: 'drip-hydration',
    title: 'Drip Hydration Intake Form',
    description: 'Complete this form before your first IV drip treatment.',
    icon: <HealthAndSafetyIcon fontSize="large" color="primary" />,
    url: 'https://hydralyfe.snapforms.com.au/form/drip-hydration-intake-beautyemergency',
    category: 'intake'
  },
  {
    id: 'integrative-health',
    title: 'Integrative Health Form',
    description: 'Comprehensive health assessment for personalized treatment plans.',
    icon: <MedicationIcon fontSize="large" color="primary" />,
    url: 'https://hydralyfe.snapforms.com.au/form/integrative-health-beautyemergency',
    category: 'intake'
  },
  {
    id: 'prescription',
    title: 'Prescription Request',
    description: 'Request a prescription renewal or new medication.',
    icon: <ReceiptIcon fontSize="large" color="primary" />,
    url: 'https://hydralyfe.snapforms.com.au/form/prescription-request-beautyemergency-new',
    category: 'request'
  },
  {
    id: 'nutrient-order',
    title: 'Nutrient Order Form',
    description: 'Order nutrient supplements for your treatment plan.',
    icon: <DescriptionIcon fontSize="large" color="primary" />,
    url: 'https://hydralyfe.snapforms.com.au/form/nutrient-order-beautyemergency',
    category: 'order'
  }
];

interface FormDialogProps {
  open: boolean;
  onClose: () => void;
  form: {
    title: string;
    url: string;
  };
}

const FormDialog: React.FC<FormDialogProps> = ({ open, onClose, form }) => {
  const [loading, setLoading] = useState(true);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{form.title}</Typography>
          <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, height: '100%' }}>
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1,
              bgcolor: 'rgba(255, 255, 255, 0.7)'
            }}
          >
            <CircularProgress />
          </Box>
        )}
        <iframe
          src={form.url}
          title={form.title}
          width="100%"
          height="100%"
          style={{ border: 'none' }}
          onLoad={() => setLoading(false)}
        />
      </DialogContent>
    </Dialog>
  );
};

const FormsTabPanel: React.FC = () => {
  const { user } = useAuth();
  const [selectedForm, setSelectedForm] = useState<{title: string, url: string} | null>(null);

  const handleOpenForm = (form: {title: string, url: string}) => {
    setSelectedForm(form);
  };

  const handleCloseForm = () => {
    setSelectedForm(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="body1" paragraph>
        Complete these forms to help us provide you with the best care. All information is securely stored and handled according to our privacy policy.
      </Typography>
      
      <Grid container spacing={3} sx={{ mt: 2 }}>
        {FORMS.map((form) => (
          <Grid item xs={12} sm={6} md={4} key={form.id}>
            <Card 
              elevation={3}
              sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6
                }
              }}
            >
              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                  {form.icon}
                </Box>
                <Typography variant="h6" component="h2" align="center" gutterBottom>
                  {form.title}
                </Typography>
                <Box sx={{ mb: 1, mt: 'auto' }}>
                  <Chip 
                    label={form.category === 'intake' ? 'Intake Form' : form.category === 'request' ? 'Request Form' : 'Order Form'} 
                    size="small"
                    color={form.category === 'intake' ? 'primary' : form.category === 'request' ? 'secondary' : 'success'}
                    sx={{ mb: 1 }}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" align="center">
                  {form.description}
                </Typography>
              </CardContent>
              <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                <Button 
                  variant="contained" 
                  color="primary"
                  onClick={() => handleOpenForm(form)}
                >
                  Open Form
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
      
      {selectedForm && (
        <FormDialog
          open={Boolean(selectedForm)}
          onClose={handleCloseForm}
          form={selectedForm}
        />
      )}
    </Box>
  );
};

const JobsTabPanel: React.FC = () => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [formTypeFilter, setFormTypeFilter] = useState('all');
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [formDataDialogOpen, setFormDataDialogOpen] = useState(false);

  // Extract unique form types and statuses for filter dropdowns
  const uniqueFormTypes = ['all', ...new Set(jobs.map(job => job.formType))];
  const uniqueStatuses = ['all', ...new Set(jobs.map(job => job.status))];

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const result = await getJobs();
      setJobs(result.jobs);
      setError(result.error);
    } catch (err) {
      setError('An unexpected error occurred');
      console.error('Error fetching jobs:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();

    // Auto-refresh every 5 minutes
    const interval = setInterval(fetchJobs, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Filter jobs based on search term, dropdown filters, and restrict to Beauty Emergency jobs only
  const filteredJobs = jobs.filter(job => {
    // Only show Beauty Emergency jobs (formSheetName starting with BEC)
    const isBeautyEmergencyJob = job.formSheetName && job.formSheetName.startsWith('BEC');
    
    const matchesSearch = job.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.formType.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (job.assignedTo && job.assignedTo.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = statusFilter === 'all' || job.status === statusFilter;
    const matchesFormType = formTypeFilter === 'all' || job.formType === formTypeFilter;
    
    return isBeautyEmergencyJob && matchesSearch && matchesStatus && matchesFormType;
  });

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleStatusFilterChange = (event: SelectChangeEvent) => {
    setStatusFilter(event.target.value);
  };

  const handleFormTypeFilterChange = (event: SelectChangeEvent) => {
    setFormTypeFilter(event.target.value);
  };

  const toggleRowExpanded = (jobId: string) => {
    setExpandedRows(prev => ({
      ...prev,
      [jobId]: !prev[jobId]
    }));
  };

  const handleOpenFormData = (job: Job) => {
    setSelectedJob(job);
    setFormDataDialogOpen(true);
  };

  const handleCloseFormData = () => {
    setFormDataDialogOpen(false);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary">
          Showing Beauty Emergency Clinic request jobs only
        </Typography>
        <Button 
          variant="contained" 
          startIcon={<RefreshIcon />} 
          onClick={fetchJobs}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          label="Search"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={handleSearchChange}
          sx={{ flexGrow: 1, minWidth: '200px' }}
        />
        <FormControl sx={{ minWidth: '150px' }} size="small">
          <InputLabel id="status-filter-label">Status</InputLabel>
          <Select
            labelId="status-filter-label"
            value={statusFilter}
            label="Status"
            onChange={handleStatusFilterChange}
          >
            {uniqueStatuses.map(status => (
              <MenuItem key={status} value={status}>{status === 'all' ? 'All Statuses' : status}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: '150px' }} size="small">
          <InputLabel id="form-type-filter-label">Form Type</InputLabel>
          <Select
            labelId="form-type-filter-label"
            value={formTypeFilter}
            label="Form Type"
            onChange={handleFormTypeFilterChange}
          >
            {uniqueFormTypes.map(type => (
              <MenuItem key={type} value={type}>{type === 'all' ? 'All Types' : formatFormType(type)}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3 }}
        >
          {error}
        </Alert>
      )}

      {loading && jobs.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell width="40px"></TableCell>
                <TableCell><strong>Client</strong></TableCell>
                <TableCell><strong>Form Type</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Submitted</strong></TableCell>
                <TableCell><strong>Last Updated</strong></TableCell>
                <TableCell><strong>Assigned To</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredJobs.length > 0 ? (
                filteredJobs.map(job => (
                  <React.Fragment key={job.uuid}>
                    <TableRow 
                      sx={{ 
                        '& > *': { borderBottom: 'unset' },
                        backgroundColor: expandedRows[job.uuid] ? 'rgba(0, 0, 0, 0.03)' : 'inherit'
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton
                            aria-label="expand row"
                            size="small"
                            onClick={() => toggleRowExpanded(job.uuid)}
                            disabled={!job.statusHistory || job.statusHistory.length === 0}
                          >
                            {expandedRows[job.uuid] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                          <Tooltip title="View Form Details">
                            <IconButton
                              aria-label="view form details"
                              size="small"
                              onClick={() => handleOpenFormData(job)}
                              color="primary"
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell>{job.clientName}</TableCell>
                      <TableCell>{formatFormType(job.formType)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Chip 
                            label={job.status} 
                            color={getStatusColor(job.status) as any}
                            size="small"
                          />
                          {job.subStatus && (
                            <Typography variant="caption" color="text.secondary">
                              {job.subStatus.replace(/_/g, ' ')}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{formatDate(job.submittedDate)}</TableCell>
                      <TableCell>{formatDate(job.statusUpdatedAt)}</TableCell>
                      <TableCell>{job.assignedTo || 'Unassigned'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                        <Collapse in={expandedRows[job.uuid]} timeout="auto" unmountOnExit>
                          <Box sx={{ margin: 2, ml: 4 }}>
                            <Typography variant="h6" gutterBottom component="div">
                              Status History
                            </Typography>
                            {job.statusHistory && job.statusHistory.length > 0 ? (
                              <Timeline sx={{ px: 0 }}>
                                {[...job.statusHistory]
                                  .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
                                  .map((entry, index) => (
                                    <TimelineItem key={index}>
                                      <TimelineOppositeContent sx={{ flex: 0.25, minWidth: '140px' }}>
                                        <Typography variant="body2" color="text.secondary">
                                          {formatDate(entry.timestamp)}
                                        </Typography>
                                      </TimelineOppositeContent>
                                      
                                      <TimelineSeparator>
                                        <TimelineDot color={getTimelineDotColor(entry.status)} />
                                        {index < job.statusHistory!.length - 1 && <TimelineConnector />}
                                      </TimelineSeparator>
                                      
                                      <TimelineContent sx={{ py: '12px', px: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                          <Typography variant="body1" component="span">
                                            {entry.status}
                                            {entry.subStatus && (
                                              <Typography 
                                                variant="caption" 
                                                color="text.secondary" 
                                                component="span" 
                                                sx={{ ml: 1 }}
                                              >
                                                {entry.subStatus.replace(/_/g, ' ')}
                                              </Typography>
                                            )}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            by {entry.updatedBy}
                                          </Typography>
                                        </Box>
                                        
                                        {entry.note && (
                                          <Typography variant="body2" color="text.secondary">
                                            "{entry.note}"
                                          </Typography>
                                        )}
                                      </TimelineContent>
                                    </TimelineItem>
                                  ))}
                              </Timeline>
                            ) : (
                              <Typography variant="body2" color="text.secondary" sx={{ my: 2, textAlign: 'center' }}>
                                No status history available for this job.
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body1" sx={{ py: 2 }}>
                      No jobs found matching the current filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {/* Form Data Dialog */}
      <JobFormDataDialog
        job={selectedJob}
        open={formDataDialogOpen}
        onClose={handleCloseFormData}
      />
    </Box>
  );
};

const RequestCenter: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const location = useLocation();
  
  // Parse URL params to set initial tab
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    
    if (tabParam === '1') {
      setTabValue(1);
    } else if (tabParam === '0') {
      setTabValue(0);
    }
  }, [location.search]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Paper elevation={3} sx={{ p: 0, mb: 3 }}>
      <Box sx={{ 
        borderBottom: 1, 
        borderColor: 'divider', 
        mb: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        pl: 3,
        pr: 3
      }}>
        <Typography variant="h4" component="h1" sx={{ py: 2 }}>
          {tabValue === 0 ? "Request Center" : "Request Status"}
        </Typography>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          aria-label="request center tabs"
          sx={{ pt: 1 }}
        >
          <Tab label="Submit Forms" id="request-center-tab-0" />
          <Tab label="Request Status" id="request-center-tab-1" />
        </Tabs>
      </Box>
      
      <Box role="tabpanel" hidden={tabValue !== 0} id="request-center-tabpanel-0">
        {tabValue === 0 && <FormsTabPanel />}
      </Box>
      
      <Box role="tabpanel" hidden={tabValue !== 1} id="request-center-tabpanel-1">
        {tabValue === 1 && <JobsTabPanel />}
      </Box>
    </Paper>
  );
};

export default RequestCenter;